// Dummy data for baby food safety campaign

export const campaignData = {
  title: "Fix the Formula",
  subtitle: "Help us test baby formula for toxic chemicals and heavy metals",
  partnership: "In partnership with OpenLabel Research Foundation",
  goal: 60000,
  raised: 12750,
  donorCount: 327,
  lastUpdated: "2 hours ago"
};

export const milestones = [
  {
    id: 1,
    amount: 15000,
    title: "Test 5 popular formulas",
    description: "Test 5 popular formulas for toxins, heavy metals, and more"
  },
  {
    id: 2,
    amount: 30000,
    title: "Test 5 popular US formulas",
    description: "Test 5 popular US formulas for toxins, heavy metals, and more"
  },
  {
    id: 3,
    amount: 45000,
    title: "Test 5 popular EU formulas",
    description: "Test 5 popular EU formulas for toxins, heavy metals, and more"
  },
  {
    id: 4,
    amount: 60000,
    title: "Test 5 organic & inorganic formulas",
    description: "Test 5 more organic and inorganic formulas for toxins, heavy metals, and more"
  },
  {
    id: 5,
    amount: 100000, // Will display as $60,000 - $100,000 range
    title: "Test breastmilk storage options",
    description: "Understand how storage and bottles may contaminate breastmilk"
  }
];

export const storyContent = {
  title: "Why This Matters",
  paragraphs: [
    "Most parents assume baby formula is safe. But recent investigations by Consumer Reports and others have found toxic substances like arsenic, lead, mercury, plastics, pesticides, and other contaminants in popular brands ([1](https://www.consumerreports.org/babies-kids/baby-formula/baby-formula-contaminants-test-results-a7140095293/), [2](https://www.plasticlist.org/), [3](https://www.ewg.org/news-insights/news/2025/03/consumer-reports-testing-finds-forever-chemicals-baby-formula), [4](https://www.parents.com/baby-formula-heavy-metals-report-11699267)). Even at trace levels, these chemicals can harm a baby's developing brain, behavior, and hormones.",
    "Baby formula is heavily regulated, just not for what you'd expect. The Infant Formula Act of 1980 was written to prevent nutrient deficiencies, not protect babies from modern threats like heavy metals, pesticide residues, or chemical byproducts ([5](https://en.wikipedia.org/wiki/Infant_Formula_Act_of_1980)).",
    "We're looking for the good stuff and the bad stuff. Parents deserve to know if a formula is missing nutrients it claims to have or is contaminated. This testing is the most comprehensive publicly shared open dataset on baby formula.",
    "Right now, there's no requirement for manufacturers to test for all of this and share results with the public. We're changing that.",
    "We tested each formula independently in partnership with an ISO 17025-accredited lab for:\n• Macronutrients\n• Minerals\n• Heavy metals ([6](https://pmc.ncbi.nlm.nih.gov/articles/PMC9271943/))\n• Glyphosate & AMPA ([7](https://www.news-medical.net/news/20250121/Study-links-increased-glyphosate-exposure-to-harmful-effects-on-infant-health.aspx))\n• BPA / BPS\n• Phthalates\n• Mycotoxins\n• Pesticides",
  ],
  videoEmbed: "https://www.youtube.com/embed/dQw4w9WgXcQ", // Placeholder
  images: [
    {
      src: "/images/holle.png",
      alt: "Holle Goat Milk Formula Stage 1",
      url: "https://www.holle.ch/en/product/organic-infant-formula-1/"
    },
    {
      src: "/images/enfamil.png",
      alt: "Enfamil NeuroPro Infant Formula Powder",
      url: "https://www.enfamil.com/products/enfamil-neuropro-infant-formula/powder-tub-20-7-oz-tub/?mediaPopup=0"
    },
    {
      src: "/images/bobbie.png",
      alt: "Bobbie Organic Infant Formula Milk Based With Iron", 
      url: "https://www.hibobbie.com/products/bobbie-organic-infant-formula"
    },
    {
      src: "/images/similac.png",
      alt: "Similac 360 Total Care Powder",
      url: "https://www.similac.com/products/baby-formula/360-total-care-powder/30-8oz-6pack.html"
    },
    {
      src: "/images/kendamil.png",
      alt: "Kendamil Organic Infant Formula",
      url: "https://www.target.com/p/kendamil-organic-infant-formula-powder-28-2oz/-/A-86918586#lnk=sametab"
    }
  ]
};

export const organizerInfo = {
  name: "Julia Lipton",
  title: "Campaign Organizer",
  bio: "I'm a mom. My friends and I spend a lot of time optimizing our health, so I assumed we'd do the same for our babies. But when I dug into formula, I was shocked: nearly half contain contaminants, and there's almost no independent public data.\n\nProfessionally, I started Awesome People Ventures, an early-stage fund investing in health, B2B SaaS, and crypto. Before that, I ran growth at Rise, a nutrition startup acquired by One Medical. At One Medical, I built their Video Visits product.\n\nMy goal with Fix the Formula is to bring data, transparency, and accountability to the food system for the next generation.",
  twitterHandle: "@JuliaLipton",
  twitterUrl: "https://twitter.com/JuliaLipton", // TODO: Replace with actual Twitter URL
  photo: "/images/9d9016bb-4d8a-45a2-934e-fef037410fdc.png"
};

export const recentDonors = [
  {
    id: 1,
    name: "Sarah M.",
    amount: 50,
    timeAgo: "2 minutes ago",
    message: "Thank you for keeping our babies safe! 💕"
  },
  {
    id: 2,
    name: "Michael R.",
    amount: 100,
    timeAgo: "15 minutes ago",
    message: "As a new dad, this is so important to me."
  },
  {
    id: 3,
    name: "Jessica L.",
    amount: 25,
    timeAgo: "32 minutes ago",
    message: "Every baby deserves safe food!"
  },
  {
    id: 4,
    name: "Anonymous",
    amount: 250,
    timeAgo: "1 hour ago",
    message: "Keep fighting the good fight! 🍼"
  },
  {
    id: 5,
    name: "David & Emma K.",
    amount: 75,
    timeAgo: "2 hours ago",
    message: "For our little one on the way ❤️"
  },
  {
    id: 6,
    name: "Lisa P.",
    amount: 40,
    timeAgo: "3 hours ago",
    message: "Transparency is everything!"
  },
  {
    id: 7,
    name: "Marcus W.",
    amount: 150,
    timeAgo: "4 hours ago",
    message: ""
  },
  {
    id: 8,
    name: "Amy S.",
    amount: 30,
    timeAgo: "5 hours ago",
    message: "So grateful for this work 🙏"
  }
];

export const donationAmounts = [25, 50, 100, 250];

export const footerLinks = [
  { label: "Privacy Policy", href: "#", placeholder: true },
  { label: "Contact Us", href: "#", placeholder: true }
];